import type { ComponentChildren } from "preact";
import { useLayoutEffect, useRef } from "preact/hooks";
import { isPlainClick, navigate } from "../lib/router.ts";
import { moving, shift } from "../lib/shift.ts";
import { Link } from "./Button.tsx";

interface TabLink {
  href: string;
  label: ComponentChildren;
  /** Beside the tab's link (not in it): counts that link elsewhere, say. */
  after?: ComponentChildren;
  current: boolean;
  /** Consecutive tabs of a group are labelled together (and set apart from the group before). */
  group?: string;
  /** Only where the page is narrow (one column, in its `@container/main`): what's beside it otherwise. */
  narrow?: boolean;
}

/** The bar named apart in a view transition (where `moving`): it stays, its labels cross-fading, rather than fading
 *  out and in with the page. */
const held = (line: string) => (moving() ? { viewTransitionName: `${line}-bar` } : undefined);

/** Another tab shown as a view transition where `moving`: the page cross-fading under the bar, the selected tab's
 *  line sliding to it. Elsewhere, at once (the link's own). */
function goTo(e: MouseEvent, href: string): void {
  if (!(isPlainClick(e) && moving())) {
    return;
  }
  e.preventDefault();
  shift(() => navigate(href, false, null, true));
}

/** One tab: its link, what's beside it, and its line when it's selected (`line`: its name in view transitions). */
function Tab({ x, line }: { x: TabLink; line: string }) {
  return (
    <div
      class={`relative -mb-px flex items-center gap-1.5 border-b-2 border-transparent pb-2 ${x.narrow ? "@3xl/main:hidden" : ""}`}
    >
      <Link
        href={x.href}
        lateral={true}
        aria-current={x.current ? "page" : undefined}
        onClick={x.current ? undefined : (e) => goTo(e, x.href)}
        class="px-1 text-sm font-medium whitespace-nowrap text-zinc-600 hover:text-zinc-900 aria-[current=page]:text-blue-700"
      >
        {x.label}
      </Link>
      {x.after}
      {x.current ? (
        <span
          aria-hidden="true"
          class="absolute inset-x-0 -bottom-0.5 h-0.5 bg-blue-600"
          style={moving() ? { viewTransitionName: line } : undefined}
        />
      ) : null}
    </div>
  );
}

/** The selected tab in `nav` in sight: scrolled sideways to it (the page not moved), in the row that scrolls (the bar,
 *  or its group's), when they don't all fit. */
function inSight(nav: HTMLElement | null): void {
  const on = nav?.querySelector<HTMLElement>("[aria-current=page]");
  const row = on?.closest<HTMLElement>("[data-tabrow]");
  if (row && on && row.scrollWidth > row.clientWidth) {
    row.scrollLeft = on.parentElement ? on.parentElement.offsetLeft - (row.clientWidth - on.offsetWidth) / 2 : 0;
  }
}

/** Consecutive tabs of the same group, together. */
function grouped(tabs: readonly TabLink[]): [string, TabLink[]][] {
  const out: [string, TabLink[]][] = [];
  for (const x of tabs) {
    const last = out.at(-1);
    if (last && last[0] === (x.group ?? "")) {
      last[1].push(x);
    } else {
      out.push([x.group ?? "", [x]]);
    }
  }
  return out;
}

/**
 * Tabs as links: the selected tab is part of the URL (a path, or `?tab=` for views of one page), so reloading, the
 * back button and shared links land on it. Grouped tabs (labelled) wrap a group at a time to a new row when they
 * don't fit, each with its line, rather than scrolling sideways; on a phone each group is a row of its own, scrolling
 * sideways on its own. The selected tab is scrolled into sight.
 */
export function TabNav({
  label,
  tabs,
  end,
}: {
  label: string;
  tabs: readonly TabLink[];
  /** At the bar's end, on its line (the shown tab's tools): the tabs scroll sideways before it's squeezed. */
  end?: ComponentChildren;
}) {
  const bar = useRef<HTMLElement>(null);
  // Its selected tab's line: named after the bar (the same on each page it's on: another page's bar becomes it in a
  // view transition), apart from another bar's (a page's tabs inside a page's).
  const line = `tab-line-${CSS.escape(label)}`;
  const shown = tabs.find((x) => x.current)?.href;
  // The selected tab in sight, each time another is.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `shown` is when to, not what's used.
  useLayoutEffect(() => inSight(bar.current), [shown]);
  if (!tabs.some((x) => x.group)) {
    return (
      <div class="mb-6 flex items-end gap-4 border-b border-zinc-200" data-tabline="" style={held(line)}>
        <nav
          ref={bar}
          aria-label={label}
          data-tabrow=""
          class="relative flex min-w-0 gap-4 overflow-x-auto overflow-y-hidden overscroll-x-contain"
        >
          {tabs.map((x) => (
            <Tab key={x.href} x={x} line={line} />
          ))}
        </nav>
        {end ? <div class="ml-auto shrink-0 pb-1.5">{end}</div> : null}
      </div>
    );
  }
  return (
    <nav
      ref={bar}
      aria-label={label}
      class="mb-6 flex flex-wrap items-end gap-y-3 phone:max-w-full phone:min-w-0 phone:flex-col phone:items-stretch"
      style={held(line)}
    >
      {grouped(tabs).map(([group, list], i, all) => (
        // The last group's line runs to the end.
        <div
          key={group}
          data-tabrow=""
          class={`relative flex flex-wrap items-end gap-x-4 gap-y-3 border-b border-zinc-200 pr-6 phone:max-w-full phone:min-w-0 phone:flex-nowrap phone:overflow-x-auto phone:overflow-y-hidden phone:overscroll-x-contain ${i === all.length - 1 ? "grow" : ""}`}
        >
          <span class="pb-2 text-xs font-medium tracking-wide whitespace-nowrap text-zinc-400 uppercase">{group}</span>
          {list.map((x) => (
            <Tab key={x.href} x={x} line={line} />
          ))}
        </div>
      ))}
    </nav>
  );
}
